import React from 'react';
import type {
  InlineMessageKeyTypes,
  ModalMessageKeyTypes,
  SubscriptionMessage,
  SubscriptionMessageKeyTypes,
} from 'redux/modules/subscriptions.types';
import {
  FEATURE_OR_LIMIT_NAME,
  MEMORY_LIMIT,
  OWNER_NAME,
  SENDER_NAME,
} from 'redux/modules/subscriptions.types';
import type { BoxFeature } from 'types/account';
import { pluralize } from 'utils/text-helper';
import { notNull } from 'utils/typesafe';

type SubscriptionModalMessagesDictionary = {
  [_messageKey in ModalMessageKeyTypes]: SubscriptionMessage;
};

type SubscriptionStateMessagesDictionary = {
  [_messageKey in InlineMessageKeyTypes]: SubscriptionMessage;
};

const contactAccountManagerLimitTemplate = `
Hi Timely team,

It looks like I’ve hit a {${FEATURE_OR_LIMIT_NAME}} on my account.

Please could you advise me on how to increase it?

Thanks,
{${SENDER_NAME}}
`;

const contactAccountManagerFeatureTemplate = `
Hi Timely team,

It looks like I’m missing {${FEATURE_OR_LIMIT_NAME}} on my account.

Please could you advise me on how to get access?

Thanks,
{${SENDER_NAME}}
`;

const contactWorkspaceAdminLimitTemplate = `
Hi {${OWNER_NAME}},

We’ve just hit a {${FEATURE_OR_LIMIT_NAME}} on our Timely account.

Do we have any plans to upgrade our subscription at all? We can’t add more until we do.

Thanks,
{${SENDER_NAME}}
`;

const contactWorkspaceAdminMemoryLimitTemplate = `
Hi {${OWNER_NAME}},

We’ve just hit a {${FEATURE_OR_LIMIT_NAME}} on our Timely account.

Do we have any plans to upgrade our subscription at all? We can't review past memories
beyond the last {${MEMORY_LIMIT}} days until we do.

Thanks,
{${SENDER_NAME}}
`;

const contactWorkspaceAdminCustomUserLimitReachedTemplate = `
Hi {${OWNER_NAME}},

We’ve just hit a {${FEATURE_OR_LIMIT_NAME}} on our Timely account.

Do we have any plans to buy extra seats or upgrade our subscription at all? We can’t add more until we do.

Thanks,
{${SENDER_NAME}}
`;

const contactWorkspaceAdminFeatureTemplate = `
Hi {${OWNER_NAME}},

I noticed {${FEATURE_OR_LIMIT_NAME}} is not available on our current Timely plan.

Do we have any plans to upgrade our subscription at all? This feature would be extremely useful, if so.

Thanks,
{${SENDER_NAME}}
`;

const contactAccountManagerTrialEndedTemplate = `
Hi Timely team,

It looks like my trial has expired.

Please could you advise me on how to subscribe?

Thanks,
{${SENDER_NAME}}
`;

const contactWorkspaceAdminTrialEndedTemplate = `
Hi {${OWNER_NAME}},

It appears as though our Timely free trial has now ended.

Would you please upgrade our subscription by heading to the Subscriptions page in Timely?
https://support.timelyapp.com/en/articles/4470012-setting-up-your-timely-subscription

This way, we can continue tracking time. 🎉

Thanks,
{${SENDER_NAME}}
`;

const contactAccountManagerSubscriptionEndedTemplate = `
Hi Timely team,

Looks like our Timely subscription has expired.

Please could you advise me on how to subscribe?

Thanks,
{${SENDER_NAME}}
`;

const contactWorkspaceAdminSubscriptionEndedTemplate = `
Hi {${OWNER_NAME}},

It appears as though our Timely Subscription has now expired.

Would you please renew our subscription by heading to the Subscriptions page in Timely?
https://support.timelyapp.com/en/articles/4470012-setting-up-your-timely-subscription

This way, we can continue tracking time. 🎉

Thanks,
{${SENDER_NAME}}
`;

const DEFAULT_SUBJECT = 'Upgrading our Timely workspace';
export const CONTACT_ACCOUNT_MANAGER_EMAIL = 'enterprise@timelyapp.com';

export const SubscriptionStateMessages: SubscriptionStateMessagesDictionary = {
  memoryRequiresUpgrade: {
    trackingKey: 'memory history',
    featureOrLimitName: 'Memory history limit',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerLimitTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminMemoryLimitTemplate,
    },
    title: 'See all your memories',
    description: {
      default: subscriptionInfo => {
        if (subscriptionInfo.memoryHistoryDays === 1) {
          return (
            <>
              Currently, you can only review today's memories. To see your full activity history,
              upgrade your subscription
            </>
          );
        }
        return (
          <>
            Currently, you can only review memories for the last{' '}
            {subscriptionInfo.memoryHistoryDays} days. To see your full activity history, upgrade
            your subscription.
          </>
        );
      },
      limited: {
        default: subscriptionInfo => {
          if (subscriptionInfo.memoryHistoryDays === 1) {
            return (
              <>
                Currently, you can only review today's memories. To see your full activity history,
                contact your workspace admin to upgrade your subscription.
              </>
            );
          }
          return (
            <>
              Currently, you can only review memories for the last{' '}
              {subscriptionInfo.memoryHistoryDays} days. To see your full activity history, contact
              your workspace admin to upgrade your subscription.
            </>
          );
        },
      },
    },
    isDismissable: false,
  },
  logForOthersRequiresUpgrade: {
    trackingKey: '"log hours for others"',
    featureOrLimitName: 'ability to log hours for others',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    description: {
      default: <>Logging hours for others is available after upgrade your subscription plan</>,
    },
    isDismissable: false,
  },
  usersDashboardRequiresUpgrade: {
    trackingKey: '"people dashboard"',
    featureOrLimitName: '"people dashboard"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    description: {
      default: (
        <>
          Unlock the People dashboard for a complete overview of company-wide performance. Monitor
          overtime and capacity, understand your billable ratio, and quickly notify people with
          incomplete timesheets.
        </>
      ),
    },
    isDismissable: false,
  },
  projectsDashboardRequiresUpgrade: {
    trackingKey: '"project dashboard"',
    featureOrLimitName: '"project dashboard"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    description: {
      default: (
        <>
          Unlock the Project Dashboard for a real-time overview of budget spend and team activity on
          your highest priority projects.
        </>
      ),
    },
    isDismissable: false,
  },
};

export const SubscriptionModalMessages: SubscriptionModalMessagesDictionary = {
  projectLimitReached: {
    trackingKey: 'project limit',
    featureOrLimitName: '"project limit"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerLimitTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminLimitTemplate,
    },
    title: `Project limit reached`,
    description: {
      default: (
        <>
          You’ve hit the project limit for this workspace. To create more projects, upgrade your
          subscription.
        </>
      ),
      admin: {
        nonEnterprise: {
          default: (
            <>
              You’ve hit the project limit for this workspace. To create more projects, upgrade your
              subscription.
            </>
          ),
        },
        enterprise: {
          default: (
            <>
              You’ve hit the project limit for this workspace. To create more projects, contact your
              Timely account manager.
            </>
          ),
        },
      },
      limited: {
        default: (
          <>
            You’ve hit the project limit for this workspace. To create more projects, contact your
            workspace admin to upgrade your subscription.
          </>
        ),
      },
    },
    isDismissable: true,
  },
  userLimitReached: {
    trackingKey: 'user limit',
    featureOrLimitName: '"user limit"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerLimitTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminCustomUserLimitReachedTemplate,
    },
    title: `User limit reached`,
    description: {
      default: (
        <>
          You've hit the user limit for your workspace. Buy extra seats or upgrade your plan in
          Settings {'>'} Subscriptions to access more seats.
        </>
      ),
      admin: {
        enterprise: {
          default: (
            <>
              Oops, you've hit the user limit for your workspace. To add more users, contact your
              Timely account manager.
            </>
          ),
        },
        nonEnterprise: {
          default: (
            <>
              You've hit the user limit for your workspace. Buy extra seats or upgrade your plan in
              Settings {'>'} Subscriptions to access more seats.
            </>
          ),
        },
      },
      normal: (
        <>
          You've hit the user limit for your workspace. To add more users, contact your workspace
          admin.
        </>
      ),
    },
    isDismissable: true,
  },
  teamsLimitReached: {
    trackingKey: 'teamsLimit',
    featureOrLimitName: '"teams limit"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    title: `Teams limit reached`,
    description: {
      default: (
        <>You've hit the teams limit for your workspace. To add more, upgrade your subscription</>
      ),
      admin: {
        enterprise: {
          default: (
            <>
              You've hit the teams limit for your workspace. To upgrade your subscription, contact
              your Timely account manager.
            </>
          ),
        },
      },
      limited: {
        default: (
          <>
            You've hit the teams limit for your workspace. To upgrade your subscription, contact
            your workspace admin.
          </>
        ),
      },
    },
    isDismissable: true,
  },
  featureAuditRequiresUpgrade: {
    trackingKey: 'audit',
    featureOrLimitName: '"time entry history"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    isDismissable: true,
    title: 'See changes made to entries',
    description: {
      default: (
        <>
          Minimize billing errors and improve team reporting transparency. To see all changes made
          to a time entry, upgrade your subscription.
        </>
      ),
      limited: {
        default: (
          <>
            Minimize billing errors and improve team reporting transparency. To see all changes made
            to your time entries, contact your workspace admin.
          </>
        ),
      },
      admin: {
        enterprise: {
          default: (
            <>
              Minimize billing errors and improve team reporting transparency. To see all changes
              made to a time entry, contact your Timely account manager.
            </>
          ),
        },
      },
    },
  },
  featureTeamsRequiresUpgrade: {
    trackingKey: 'teams',
    featureOrLimitName: '"Teams"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    title: `Set up teams`,
    description: {
      default: (
        <>
          Simplify team time management and reporting. To group colleagues into teams and assign
          team leads, upgrade your subscription.
        </>
      ),
      admin: {
        enterprise: {
          default: (
            <>
              Simplify team time management and reporting. To group colleagues into teams and assign
              team leads, contact your Timely account manager.
            </>
          ),
        },
      },
      limited: {
        default: (
          <>
            Simplify team time management and reporting. To group colleagues into teams and assign
            team leads, contact your workspace admin to upgrade your subscription.
          </>
        ),
      },
    },
    isDismissable: true,
  },
  subscriptionExpired: {
    trackingKey: 'subscription expired',
    featureOrLimitName: '"subscription expired"',
    upgradeText: 'Subscribe now',
    contactAccountManagerTemplate: {
      subject: 'Our Timely subscription has now expired',
      body: contactAccountManagerSubscriptionEndedTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: 'Our Timely subscription has now expired',
      body: contactWorkspaceAdminSubscriptionEndedTemplate,
    },
    title: 'Your subscription has expired',
    description: {
      default: (
        <>
          Don't worry, all your data is still intact waiting for you. To continue using Timely,
          subscribe to one of our plans.
        </>
      ),
      limited: {
        default: (
          <>
            Don't worry, all your data is still intact waiting for you. To continue using Timely,
            contact your workspace admin to subscribe.
          </>
        ),
      },
    },
    isDismissable: true,
  },
  trialEnded: {
    trackingKey: 'trial ended',
    featureOrLimitName: '"trial ended"',
    upgradeText: 'Subscribe now',
    contactAccountManagerTemplate: {
      subject: 'My Timely trial has ended',
      body: contactAccountManagerTrialEndedTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: 'My Timely trial has ended',
      body: contactWorkspaceAdminTrialEndedTemplate,
    },
    title: 'Your trial has ended',
    description: {
      default: (
        <>
          Don't worry, all your data is still intact waiting for you. To continue using Timely,
          subscribe to one of our plans.
        </>
      ),
      limited: {
        default: (
          <>
            Don't worry, all your data is still intact waiting for you. To continue using Timely,
            contact your workspace admin to subscribe.
          </>
        ),
      },
    },
    isDismissable: true,
  },
  featureHourlyRatesRequiresUpgrade: {
    trackingKey: 'hourly rates',
    featureOrLimitName: '"hourly rates"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    title: 'Set an hourly rate',
    description: {
      default: (
        <>
          Know how much money you make on every project. To start setting hourly rates for your
          work, upgrade your subscription.
        </>
      ),
      admin: {
        enterprise: {
          default: (
            <>
              Know how much money you make on every project. To start setting hourly rates for your
              work, contact your Timely account manager.
            </>
          ),
        },
      },
      limited: {
        default: (
          <>
            Know how much money you make on every project. To start setting hourly rates for your
            work, contact your workspace admin to upgrade your subscription.
          </>
        ),
      },
    },
    isDismissable: true,
  },
  featureAnomaliesRequiresUpgrade: {
    trackingKey: 'anomalies',
    featureOrLimitName: '"error detection"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    title: 'Identify logging errors',
    isDismissable: true,
    description: {
      default: (
        <>
          Correct timekeeping mistakes before they impact your billing. To get AI to identify
          unusual logging activities, upgrade your subscription.
        </>
      ),
      admin: {
        enterprise: {
          default: (
            <>
              Correct timekeeping mistakes before they impact your billing. To get AI to identify
              unusual logging activities, contact your Timely account manager.
            </>
          ),
        },
      },
      limited: {
        default: (
          <>
            Correct timekeeping mistakes before they impact your billing. To get AI to identify
            unusual logging activities, contact your workspace admin to upgrade your subscription.
          </>
        ),
      },
    },
  },
  featureBudgetRequiresUpgrade: {
    trackingKey: 'budget',
    featureOrLimitName: '"project budgets"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    title: 'Set project budgets',
    isDismissable: true,
    description: {
      default: (
        <>
          Manage retainers and understand project costs. To set one-off or recurring time and
          monetary budgets, upgrade your subscription.
        </>
      ),
      admin: {
        enterprise: {
          default: (
            <>
              Manage retainers and understand project costs. To set one-off or recurring time and
              monetary budgets, contact your Timely account manager.
            </>
          ),
        },
      },
      limited: {
        default: (
          <>
            Manage retainers and understand project costs. To set one-off or recurring time and
            monetary budgets, contact your workspace admin to upgrade your subscription.
          </>
        ),
      },
    },
  },
  featureRequiredNotesRequiresUpgrade: {
    trackingKey: 'required notes',
    featureOrLimitName: '"mandatory notes"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    title: 'Make notes mandatory',
    isDismissable: true,
    description: {
      default: (
        <>
          Never forget the fine details of your work. To make notes mandatory on time entries,
          upgrade your subscription.
        </>
      ),
      admin: {
        enterprise: {
          default: (
            <>
              Never forget the fine details of your work. To make notes mandatory on time entries,
              contact your Timely account manager.
            </>
          ),
        },
      },
      limited: {
        default: (
          <>
            Never forget the fine details of your work. To make notes mandatory on time entries,
            contact your workspace admin to upgrade your subscription.
          </>
        ),
      },
    },
  },
  featureTagRequiresUpgrade: {
    trackingKey: 'required tags',
    featureOrLimitName: '"mandatory tags"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    title: 'Make tags mandatory',
    isDismissable: true,

    description: {
      default: (
        <>
          Standardize categorization across all your hours. To make tags mandatory on time entries,
          upgrade your subscription.
        </>
      ),
      admin: {
        enterprise: {
          default: (
            <>
              Standardize categorization across all your hours. To make tags mandatory on time
              entries, contact your Timely account manager.
            </>
          ),
        },
      },
      limited: {
        default: (
          <>
            Standardize categorization across all your hours. To make tags mandatory on time
            entries, contact your workspace admin to upgrade your subscription.
          </>
        ),
      },
    },
  },
  featureIntercomSupportRequiresUpgrade: {
    trackingKey: 'intercom support',
    featureOrLimitName: '"in-app support"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    title: 'Get in-app support',
    isDismissable: true,
    description: {
      default: (
        <>
          Get faster responses and priority attention. To reach our Support team directly inside
          Timely, upgrade your subscription.
        </>
      ),
      admin: {
        enterprise: {
          default: (
            <>
              Get faster responses and priority attention. To reach our Support team directly inside
              Timely, contact your Timely account manager.
            </>
          ),
        },
      },
      limited: {
        default: (
          <>
            Get faster responses and priority attention. To reach our Support team directly inside
            Timely, contact your workspace admin to upgrade your subscription.
          </>
        ),
      },
    },
  },
  featureCompanyViewRequiresUpgrade: {
    trackingKey: 'company view',
    featureOrLimitName: '"Company view"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    title: 'Account for missing hours',
    isDismissable: true,
    description: {
      default: (
        <>
          Absence-proof timesheets for everyone in your company. To log and schedule time for
          others, upgrade your subscription.
          <br />
          <br />
          Starter plan Admins, Managers and Team Leads can log time for others from the “Users”
          dropdown in the upper-right corner of the Hours page.
        </>
      ),
      admin: {
        enterprise: {
          default: (
            <>
              Absence-proof timesheets for everyone in your company. To log and schedule time for
              others, contact your Timely account manager.
              <br />
              <br />
              Starter plan Admins, Managers and Team Leads can log time for others from the “Users”
              dropdown in the upper-right corner of the Hours page.
            </>
          ),
        },
      },
      limited: {
        default: (
          <>
            Absence-proof timesheets for everyone in your company. To log and schedule time for
            others, contact your workspace admin to upgrade your subscription.
          </>
        ),
      },
    },
  },
  featureBilledHoursRequiresUpgrade: {
    trackingKey: 'locked hours',
    featureOrLimitName: '"locked hours"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    title: "Don't bill clients twice",
    isDismissable: true,
    description: {
      default: (
        <>
          Make your accounts foolproof. To prevent editing on billed hours, upgrade your
          subscription.
        </>
      ),
      admin: {
        enterprise: {
          default: (
            <>
              Make your accounts foolproof. To prevent editing on billed hours, contact your Timely
              account manager.
            </>
          ),
        },
      },
      limited: {
        default: (
          <>
            Make your accounts foolproof. To prevent editing on billed hours, contact your workspace
            upgrade your subscription.
          </>
        ),
      },
    },
  },
  featureLoggedCostRequiresUpgrade: {
    trackingKey: 'logged cost',
    featureOrLimitName: '"internal costs"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    isDismissable: true,
    title: 'Upgrade to include logged costs in your reports',
    description: {
      default: (
        <>
          Set individual user cost rates to track project costs, allocate resources efficiently, and
          manage labor expenses. Compare internal costs with client rates for better financial
          control and profitability. <br />
          <a
            href="https://support.timelyapp.com/en/articles/6155356-how-do-i-set-or-edit-a-user-s-cost-rate"
            rel="noopener noreferer"
            target="_blank"
          >
            Learn more
          </a>
        </>
      ),
    },
  },
  featureProfitabilityRequiresUpgrade: {
    trackingKey: 'profitability',
    featureOrLimitName: '"internal costs"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    isDismissable: true,
    title: 'Upgrade to include profitability in your reports',
    description: {
      default: (
        <>
          Seamlessly track cost rates and associated revenue, providing you with real-time insights
          into your profitability. Empower your business decisions with detailed financial
          analytics, all within Timely! <br />
          <a
            href="https://support.timelyapp.com/en/articles/4699590-custom-reports-in-timely#h_9539a493c8"
            rel="noopener noreferer"
            target="_blank"
          >
            Learn more
          </a>
        </>
      ),
    },
  },
  featureProfitRequiresUpgrade: {
    trackingKey: 'profit',
    featureOrLimitName: '"internal costs"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    isDismissable: true,
    title: 'Upgrade to include profit in your reports',
    description: {
      default: (
        <>
          Seamlessly track cost rates and associated revenue, providing you with real-time insights
          into your profitability. Empower your business decisions with detailed financial
          analytics, all within Timely! <br />
          <a
            href="https://support.timelyapp.com/en/articles/4699590-custom-reports-in-timely#h_9539a493c8"
            rel="noopener noreferer"
            target="_blank"
          >
            Learn more
          </a>
        </>
      ),
    },
  },
  featureProjectNotificationsRequiresUpgrade: {
    trackingKey: 'project budget notifications',
    featureOrLimitName: '"project notifications"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    isDismissable: true,
    title: 'Get project notifications',
    description: {
      default: (
        <>Upgrade to get notified when your project reaches 50%, 80%, 100% of your budget.</>
      ),
      admin: {
        enterprise: {
          default: (
            <>
              Get notified when your project reaches 50%, 80%, 100% of your budget. Contact your
              Timely account manager.
            </>
          ),
        },
      },
    },
  },
  featureLiveReportsRequiresUpgrade: {
    trackingKey: 'live reports',
    featureOrLimitName: '"live reports"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    isDismissable: true,
    title: 'Get live reports',
    description: {
      default: (
        <>
          Share reports that update upon refresh, instead of re-exporting the same static reports
          for clients. Upgrade your subscription to publish live reports.
        </>
      ),
      admin: {
        enterprise: {
          default: (
            <>
              Share reports that update upon refresh, instead of re-exporting the same static
              reports for clients. Contact your Timely account manager to publish live reports.
            </>
          ),
        },
      },
    },
  },
  featureIndividualCapacityRequiresUpdate: {
    trackingKey: 'individual capacity',
    featureOrLimitName: '"individual capacity"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    isDismissable: true,
    title: 'Unlock Individual Capacity',
    description: {
      default: <>Individual capacity allows you to manage varying weekly hours across your team.</>,
    },
  },
  premiumIntegrationsRequiresUpgrade: {
    trackingKey: 'premium integrations',
    featureOrLimitName: '"premium integrations"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    isDismissable: true,
    title: 'Unlock Premium integrations',
    description: {
      default: <>Upgrade to the Premium plan to access integrations like QuickBooks.</>,
    },
  },
  azureADIntegrationRequiresUpgrade: {
    trackingKey: 'azure ad integration',
    featureOrLimitName: '"azure ad integration"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    isDismissable: true,
    title: 'Unlock the Azure AD integration',
    description: {
      default: (
        <>
          Workspaces on Unlimited+ plans can enable this integration by contacting Support or their
          Account Manager.
        </>
      ),
    },
  },
  featureTeamLeadRequiresUpgrade: {
    trackingKey: 'team_lead',
    featureOrLimitName: '"team leads"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    title: `Assign a team lead`,
    description: {
      default: (
        <>
          Delegate team management in Timely. To let employees manage people without granting them
          full account access, upgrade your subscription.
        </>
      ),
      admin: {
        enterprise: {
          default: (
            <>
              Delegate team management in Timely. To let employees manage teams without granting
              them full account access, contact your Timely account manager.
            </>
          ),
        },
      },
      limited: {
        default: (
          <>
            Delegate team management in Timely. To let employees manage people without granting them
            full account access, upgrade your subscription.
          </>
        ),
      },
    },
    isDismissable: true,
  },
  featureAccountBrandingRequiresUpgrade: {
    trackingKey: 'branded_reports',
    featureOrLimitName: '"branded reports"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    title: 'Personalize your reports',
    isDismissable: true,
    description: {
      default: <>Apply logos and brand colors to add a professional touch to your reports.</>,
      admin: {
        enterprise: {
          default: (
            <>
              Apply logos and brand colors to add a professional touch to your reports. To get
              access to this feature, contact your Timely account manager.
            </>
          ),
        },
      },
      limited: {
        default: (
          <>
            Apply logos and brand colors to add a professional touch to your reports. To get access
            to this feature, contact your workspace admin to upgrade your subscription.
          </>
        ),
      },
    },
  },
  featureHighLevelReportsRequiresUpgrade: {
    trackingKey: 'high_level_reports',
    featureOrLimitName: '"high level reports"',
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    title: 'Export high-level reports',
    isDismissable: true,
    description: {
      default: (
        <>
          Export high-level reports, hiding individual entries and only displaying totals. To export
          suchs reports, upgrade your subscription.
        </>
      ),
      admin: {
        enterprise: {
          default: (
            <>
              Export high-level reports, hiding individual entries and only displaying totals. To
              export suchs reports, contact your Timely account manager.
            </>
          ),
        },
      },
      limited: {
        default: (
          <>
            Export high-level reports, hiding individual entries and only displaying totals. To
            export suchs reports, contact your workspace admin to upgrade your subscription.
          </>
        ),
      },
    },
  },
  featureTasksRequiresUpgrade: {
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    trackingKey: 'tasks_add_on',
    featureOrLimitName: '"tasks add-on"',
    title: 'Your team hub for managing work in Timely',
    description: {
      default: (
        <>
          Tasks is the first stage of a new breed of planning tool powered by automatic time
          tracking. Acting as a layer between projects and timesheets, Tasks helps teams assign,
          schedule and log time against big pieces of work.
          <br />
          <br />
          Use this space to gain full visibility of upcoming tasks and work in progress across your
          team — managing deadlines, estimated durations, team capacity and real-time progress.
        </>
      ),
    },
    isDismissable: true,
  },
  featureHourStatesRequiresUpgrade: {
    contactAccountManagerTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactAccountManagerFeatureTemplate,
    },
    contactWorkspaceAdminTemplate: {
      subject: DEFAULT_SUBJECT,
      body: contactWorkspaceAdminFeatureTemplate,
    },
    trackingKey: 'hour_states',
    featureOrLimitName: 'time entry states',
    title: 'Unlock Time Entry States',
    description: {
      default: (
        <>
          Take control of your team's time tracking with custom entry statuses. Approve timesheets,
          manage workflows, mark entries as billed or locked and fitler reports effortlessly.
          <br />
          <br />
          Available on premium and unilimited plans.
        </>
      ),
    },
    isDismissable: true,
  },
};

export const getSubscriptionMessage = (
  messageKey: SubscriptionMessageKeyTypes
): SubscriptionMessage => {
  return (
    SubscriptionStateMessages[messageKey as InlineMessageKeyTypes] ||
    SubscriptionModalMessages[messageKey as ModalMessageKeyTypes]
  );
};

export const getFeaturePlanAvailability = (
  message: SubscriptionMessage,
  featuresByPlan: Record<string, Array<BoxFeature>>
) => {
  const feature = message.featureOrLimitName.split('"')[1];
  // we need to make a one off exception for Azure, as it's only available for Unlimited+
  // despite backend signalling differently https://github.com/Timely/project-management/issues/2213
  if (feature === 'azure ad integration') {
    return null;
  }

  const plansWithFeature = Object.entries(featuresByPlan)
    .map(([key, values]) => {
      if (values.find(val => val.name.toLowerCase() === feature)) {
        return key;
      }
      return null;
    })
    .filter(notNull);

  return { readableSentence: new Intl.ListFormat().format(plansWithFeature), plansWithFeature };
};

export const PlanAvailabilityMessage: React.FC<{ plans: string; numberOfPlans: number }> = ({
  plans,
  numberOfPlans,
}) => {
  return (
    <p>
      This feature is available on our {plans}{' '}
      {pluralize(numberOfPlans, {
        singular: 'plan',
        plural: 'plans',
        omitAmount: true,
      })}
      .
    </p>
  );
};
